import { cn } from "@/lib/utils";
import { UploadCloud, Share2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="overline block text-muted-foreground">{label}</span>
    <div className="mt-2">{children}</div>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </label>
);

const inputBase =
  "w-full rounded-lg border border-input bg-background px-3.5 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20";

export const TextInput = ({ className, ...props }) => (
  <input {...props} className={cn(inputBase, className)} />
);

export const TextArea = ({ className, ...props }) => (
  <textarea {...props} className={cn(inputBase, "resize-y", className)} />
);

export const Select = ({ className, children, ...props }) => (
  <select {...props} className={cn(inputBase, "cursor-pointer", className)}>{children}</select>
);

export const PrimaryButton = ({ className, children, ...props }) => (
  <button {...props} className={cn(
    "inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50",
    className
  )}>{children}</button>
);

export const GhostButton = ({ className, children, ...props }) => (
  <button {...props} className={cn(
    "inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-50",
    className
  )}>{children}</button>
);

export const ShareButton = ({ text, title = "Shared from Kovi Tools", testid = "share-btn", className }) => {
  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title, text });
      else { await navigator.clipboard.writeText(text); toast.success("Copied — ready to share"); }
    } catch (e) { /* dismissed */ }
  };
  return (
    <button data-testid={testid} onClick={share} className={cn(
      "inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted",
      className
    )}><Share2 className="h-3 w-3" /> Share</button>
  );
};

export const ResultStat = ({ label, value, accent = false }) => (
  <div className={cn("rounded-xl border p-4", accent ? "border-primary bg-primary text-primary-foreground" : "border-border bg-muted")}>
    <p className={cn("overline", accent ? "text-primary-foreground/70" : "text-muted-foreground")}>{label}</p>
    <p className="mt-1 font-heading text-2xl font-bold tracking-tight break-words">{value}</p>
  </div>
);

export const FileDrop = ({ accept, multiple = false, onFiles, label = "Drop files here or click to browse", testid = "file-drop" }) => {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const handle = (list) => { const files = Array.from(list || []); if (files.length) onFiles(files); };
  return (
    <div
      data-testid={testid}
      onClick={() => ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors",
        drag ? "border-primary bg-primary/5" : "border-border bg-muted/40 hover:border-primary/50"
      )}
    >
      <UploadCloud className="h-8 w-8 text-muted-foreground" />
      <p className="mt-3 text-sm font-medium text-foreground">{label}</p>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" onChange={(e) => handle(e.target.files)} />
    </div>
  );
};
